/**
 * MixtapeDownloadGate (iter 170)
 *
 * Replaces the direct HearThis <a> download link with a lead-gen modal.
 * Collects WhatsApp number + (optional) email + opt-in consent, posts to
 * /api/mixtapes/{id}/request-download, then triggers the actual download.
 *
 * Returning users with the same phone in localStorage skip the form and
 * download immediately. This keeps the gate's purpose (build the opt-in
 * list) without nagging already-converted fans.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Loader2, X, MessageCircle, Mail, CheckCircle2, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'rastyle_dl_lead';

const _readCachedLead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.phone ? parsed : null;
  } catch { return null; }
};

const _writeCachedLead = (lead) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(lead)); } catch { /* storage unavailable */ }
};

const _triggerDownload = (url, suggestedName) => {
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    if (suggestedName) a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 1500);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
};

const MixtapeDownloadGate = ({
  mixtapeId,
  mixtapeTitle,
  dj,
  variant = 'pill',       // 'pill' (compact card pill) | 'full' (detail page CTA)
  className = '',
  testIdSuffix = '',
}) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [waOptin, setWaOptin] = useState(true);
  const [mktOptin, setMktOptin] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cachedLead, setCachedLead] = useState(null);

  useEffect(() => {
    const cached = _readCachedLead();
    if (cached) {
      setCachedLead(cached);
      setPhone(cached.phone);
      if (cached.email) setEmail(cached.email);
    }
  }, []);

  const submit = useCallback(async (override) => {
    const phoneToUse = override?.phone || phone;
    const emailToUse = override?.email ?? email;
    if (!phoneToUse || phoneToUse.replace(/\D/g, '').length < 9) {
      toast.error('Enter a valid WhatsApp number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/mixtapes/${mixtapeId}/request-download`, {
        phone: phoneToUse,
        email: emailToUse || null,
        whatsapp_optin: waOptin,
        marketing_optin: mktOptin,
      }, { timeout: 15000 });
      const { download_url, mixtape_title, is_new_lead, phone: normalizedPhone } = res.data;
      _writeCachedLead({ phone: normalizedPhone, email: emailToUse || null });
      setCachedLead({ phone: normalizedPhone, email: emailToUse || null });
      _triggerDownload(download_url, `${(mixtape_title || 'mixtape').replace(/[^\w-]+/g, '_')}.mp3`);
      toast.success(is_new_lead
        ? '🎧 Karibu! Your download is starting…'
        : 'Welcome back — download starting…');
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not start download';
      toast.error(typeof msg === 'string' ? msg : 'Could not start download');
    } finally {
      setSubmitting(false);
    }
  }, [mixtapeId, phone, email, waOptin, mktOptin]);

  const handleClick = () => {
    // Fast path for returning users — auto-submit with the cached phone.
    if (cachedLead?.phone) {
      submit({ phone: cachedLead.phone, email: cachedLead.email });
      return;
    }
    setOpen(true);
  };

  const buttonProps = {
    onClick: handleClick,
    disabled: submitting,
    'data-testid': `mixtape-download-cta${testIdSuffix ? '-' + testIdSuffix : ''}`,
    title: cachedLead?.phone ? `Download as ${cachedLead.phone}` : `Get "${mixtapeTitle}" (MP3)`,
  };

  return (
    <>
      {variant === 'full' ? (
        <button
          {...buttonProps}
          className={`flex items-center gap-2 px-4 py-3 bg-primary/15 border border-primary/40 rounded-sm hover:bg-primary/25 text-primary transition-colors text-sm font-semibold disabled:opacity-60 ${className}`}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download MP3
        </button>
      ) : (
        <button
          {...buttonProps}
          className={`mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-primary/15 border border-primary/30 hover:bg-primary/25 text-primary text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60 ${className}`}
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          Download
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
            onClick={() => !submitting && setOpen(false)}
            data-testid="mixtape-download-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-950 border border-rastyle-orange/30 rounded-2xl max-w-md w-full p-6 relative"
            >
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                aria-label="Close"
                data-testid="mixtape-download-modal-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-rastyle-orange/15 flex items-center justify-center">
                  <Download className="w-5 h-5 text-rastyle-orange" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-rastyle-orange">Unlock download</p>
                  <h2 className="font-heading font-extrabold text-lg leading-tight truncate" title={mixtapeTitle}>
                    {mixtapeTitle}
                  </h2>
                  {dj && <p className="text-xs text-zinc-400">by {dj}</p>}
                </div>
              </div>

              <p className="text-sm text-zinc-300 mb-5">
                Drop your WhatsApp and we&apos;ll send you the download link plus a fresh reggae &amp; dancehall mix every week. No spam — reply <span className="font-mono text-rastyle-orange">STOP</span> anytime.
              </p>

              <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="space-y-3"
              >
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">
                    WhatsApp number *
                  </label>
                  <div className="relative">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="tel"
                      inputMode="tel"
                      autoFocus
                      placeholder="0712 345 678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      disabled={submitting}
                      className="w-full pl-10 pr-3 h-11 rounded-xl bg-black/40 border border-white/10 focus:border-rastyle-orange/60 focus:ring-2 focus:ring-rastyle-orange/20 outline-none text-sm font-mono"
                      data-testid="dl-gate-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">
                    Email <span className="opacity-50">(optional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                      className="w-full pl-10 pr-3 h-11 rounded-xl bg-black/40 border border-white/10 focus:border-rastyle-orange/60 focus:ring-2 focus:ring-rastyle-orange/20 outline-none text-sm"
                      data-testid="dl-gate-email"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={waOptin}
                    onChange={(e) => setWaOptin(e.target.checked)}
                    disabled={submitting}
                    className="mt-0.5 accent-rastyle-orange"
                    data-testid="dl-gate-wa-optin"
                  />
                  <span>Send me weekly mix drops on WhatsApp.</span>
                </label>

                <button
                  type="submit"
                  disabled={submitting || !phone.trim()}
                  className="w-full h-12 rounded-full bg-rastyle-orange text-black font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-orange-400 transition-colors disabled:opacity-50"
                  data-testid="dl-gate-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Unlocking…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4" /> Get my download</>
                  )}
                </button>

                <p className="text-[10px] text-zinc-500 flex items-start gap-1 mt-2">
                  <Shield className="w-3 h-3 mt-0.5 shrink-0" />
                  Your number stays private. We never share it.
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MixtapeDownloadGate;
